import { Fragment, useEffect, useMemo, useState } from "react";
import withMonthlyPaymentsAccess from "../components/withMonthlyPaymentsAccess";
import RadioGroup from "../../../DesignSystem/Inputs/RadioGroup";
import FileUpload from "../../../DesignSystem/Upload/FileUpload";
import UploadedFileList from "../../../DesignSystem/Upload/UploadedFileList";
import PrimaryButton from "../../../DesignSystem/Buttons/PrimaryButton";
import SecondaryButton from "../../../DesignSystem/Buttons/SecondaryButton";
import CustomSelect from "../../../DesignSystem/Dropdowns/CustomSelect";
import { useAdviserList } from "../../../../context/Adviser/Utils/AdviserEmailList";
import { ReactComponent as EditIcon } from "../../../../assets/icons/finance/Edit.svg";
import {
  normaliseSuspenseRows,
  parseSuspenseStatementFile,
  SUSPENSE_TABLE_HEADERS,
} from "../utils/suspenseStatementParser";
import {
  saveSuspenseAdviserLinks,
  saveSuspenseStatement,
  subscribeSuspenseStatement,
} from "../services/suspenseStatementService";
import { formatMonthKey } from "../utils/chargeDates";
import { downloadStatementTemplate, SUSPENSE_TEMPLATE_HEADERS } from "../utils/statementTemplates";

const normaliseName = (value) => String(value ?? "").trim().toLowerCase();

const SuspenseSummary = ({ selectedMonth, monthOptions, onMonthChange }) => {
  const { advisers } = useAdviserList();
  const [statement, setStatement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [editingAdviser, setEditingAdviser] = useState("");
  const [savingAdviser, setSavingAdviser] = useState(false);
  const [adviserSaveError, setAdviserSaveError] = useState("");

  const selectedMonthLabel = useMemo(
    () => formatMonthKey(selectedMonth),
    [selectedMonth],
  );
  const tableStatement = useMemo(() => {
    if (!statement) return null;
    const links = new Map((statement.adviserLinks || []).map((link) => [normaliseName(link.sourceAdviserName), link]));
    const byName = new Map(advisers.map((adviser) => [normaliseName(adviser.name), adviser]));
    const byEmail = new Map(advisers.map((adviser) => [normaliseName(adviser.email), adviser]));
    const rows = normaliseSuspenseRows(statement.rows).map((row) => {
      const override = links.get(normaliseName(row.SourceAdviserName));
      const linked = override?.email
        ? byEmail.get(normaliseName(override.email)) || override
        : byName.get(normaliseName(row.MappedAdviserName));
      return {
        ...row,
        LinkedAdviser: linked?.email
          ? `${linked.name || row.MappedAdviserName} (${linked.email})`
          : `${row.MappedAdviserName || "Unmapped"} (email not linked)`,
        LinkedAdviserEmail: linked?.email || "",
      };
    });
    return { ...statement, headers: SUSPENSE_TABLE_HEADERS, rows };
  }, [advisers, statement]);
  const adviserOptions = useMemo(() => advisers
    .map((adviser) => ({ id: adviser.email, name: `${adviser.name} (${adviser.email})` }))
    .sort((left, right) => left.name.localeCompare(right.name, "en-GB")), [advisers]);
  const headerLabel = (header) => ({
    LinkedAdviser: "Mapped Adviser",
    ClearanceStatus: "Clearance Status",
    ClientName: "Client Name",
    PolicyAccountNumber: "Policy Number",
    PolicyProvider: "Payer",
    SuspenseAmount: "Suspense Amount",
    SuspenseCharge: "Suspense Charge",
  }[header] || header);
  const isAmount = (header) => header === "SuspenseAmount" || header === "SuspenseCharge";
  const statementTotals = useMemo(() => {
    const numberValue = (value) => {
      const number = Number(String(value ?? "").replace(/[,£]/g, ""));
      return Number.isFinite(number) ? number : 0;
    };
    return (tableStatement?.rows || []).reduce((totals, row) => ({
      SuspenseAmount: totals.SuspenseAmount + numberValue(row.SuspenseAmount),
      SuspenseCharge: totals.SuspenseCharge + numberValue(row.SuspenseCharge),
    }), { SuspenseAmount: 0, SuspenseCharge: 0 });
  }, [tableStatement]);
  const formatCurrency = (value) => Number(value).toLocaleString("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const cellValue = (row, header, index) => {
    if (header === "Index") return index + 1;
    if (!isAmount(header)) return row[header] || "-";
    const amount = Number(String(row[header] ?? "").replace(/[,£]/g, ""));
    return Number.isFinite(amount)
      ? amount.toLocaleString("en-GB", { style: "currency", currency: "GBP", minimumFractionDigits: 2 })
      : row[header] || "-";
  };

  const changeLinkedAdviser = async (row, email) => {
    const adviser = advisers.find((item) => normaliseName(item.email) === normaliseName(email));
    if (!adviser) return;
    const links = (statement.adviserLinks || [])
      .filter((link) => normaliseName(link.sourceAdviserName) !== normaliseName(row.SourceAdviserName));
    links.push({ sourceAdviserName: row.SourceAdviserName, adviserName: adviser.name, email: adviser.email });
    setSavingAdviser(true);
    setAdviserSaveError("");
    try {
      await saveSuspenseAdviserLinks(selectedMonth, links);
      setEditingAdviser("");
    } catch {
      setAdviserSaveError("Unable to update the linked adviser email.");
    } finally {
      setSavingAdviser(false);
    }
  };

  useEffect(() => {
    setLoading(true);
    setLoadError("");
    setStatement(null);
    return subscribeSuspenseStatement(
      selectedMonth,
      (nextStatement) => {
        setStatement(nextStatement);
        setLoading(false);
      },
      () => {
        setLoadError("Unable to load the Suspense statement.");
        setLoading(false);
      },
    );
  }, [selectedMonth]);

  const closeUpload = () => {
    if (uploading) return;
    setIsUploadOpen(false);
    setSelectedFile(null);
    setUploadError("");
  };

  const uploadStatement = async () => {
    setUploading(true);
    setUploadError("");
    try {
      const parsed = await parseSuspenseStatementFile(selectedFile);
      await saveSuspenseStatement({
        month: selectedMonth,
        file: selectedFile,
        previousStoragePath: statement?.storagePath || "",
        ...parsed,
      });
      setUploading(false);
      setIsUploadOpen(false);
      setSelectedFile(null);
    } catch (error) {
      setUploadError(error.message || "Unable to import the selected statement.");
      setUploading(false);
    }
  };

  return (
    <section aria-label="Suspense" data-month={selectedMonth} className="column gap-l quilter-summary">
      <header className="row space-between align-center gap-l">
        <h3 className="FP_H3">Suspense</h3>
        <div className="row align-center gap-s">
          <SecondaryButton
            type="button"
            label="DOWNLOAD TEMPLATE"
            onClick={() => downloadStatementTemplate({
              headers: SUSPENSE_TEMPLATE_HEADERS,
              fileName: "Suspense_Statement_Template.xlsx",
              sheetName: "Suspense Statement",
            })}
          />
          <PrimaryButton
            type="button"
            label={statement ? "REUPLOAD SUSPENSE STATEMENT" : "UPLOAD SUSPENSE STATEMENT"}
            onClick={() => setIsUploadOpen(true)}
          />
        </div>
      </header>
      <div role="group" aria-label="Suspense month">
        <RadioGroup
          name="suspense-month"
          options={monthOptions}
          value={selectedMonth}
          onChange={(event) => onMonthChange(event.target.value)}
          inline
        />
      </div>

      {loading && <p className="FP_Content" role="status">Loading Suspense statement...</p>}
      {!loading && loadError && <p className="FP_Content text-error" role="alert">{loadError}</p>}
      {adviserSaveError && <p className="FP_Content text-error" role="alert">{adviserSaveError}</p>}
      {!loading && !loadError && !statement && (
        <p className="FP_Content" role="status">No Suspense statement has been uploaded for {selectedMonthLabel}.</p>
      )}
      {!loading && statement && (
        <>
          <p className="FP_Help_Text black">
            <a className="text-primary-1 quilter-file-link" href={tableStatement.fileUrl} download={tableStatement.fileName}>
              {tableStatement.fileName}
            </a> · {tableStatement.rowCount ?? tableStatement.rows?.length ?? 0} entries
          </p>
          <section className="website-dashboard-card table-scroll-card quilter-statement-card">
            <div className="website-table-wrap table-rows-scroll-container table-scroll-container">
              <table className="website-table quilter-statement-table" aria-label={`Suspense statement for ${selectedMonthLabel}`}>
                <thead><tr>{tableStatement.headers.map((header) => (
                  <Fragment key={header}>
                    <th className={`FP_Help_Text_Semibold uppercase ${isAmount(header) ? "text-align-right" : ""}`}>{headerLabel(header)}</th>
                    {header === "Index" && <th className="FP_Help_Text_Semibold uppercase quilter-action-column">Edit</th>}
                  </Fragment>
                ))}</tr></thead>
                <tbody>{tableStatement.rows.map((row, rowIndex) => (
                  <Fragment key={`${rowIndex}-${row.SourceAdviserName}`}>
                    <tr className={!row.LinkedAdviserEmail ? "monthly-payment-unlinked-email-row" : ""}>{tableStatement.headers.map((header) => (
                      <Fragment key={header}>
                        <td className={`FP_Help_Text black ${isAmount(header) ? "text-align-right" : ""}`}>
                          {header === "LinkedAdviser" ? row.LinkedAdviser : cellValue(row, header, rowIndex)}
                        </td>
                        {header === "Index" && (
                          <td className="quilter-action-column">
                            <button
                              type="button"
                              className="table-row-action"
                              aria-label={`Edit linked adviser for ${row.SourceAdviserName}`}
                              aria-expanded={editingAdviser === rowIndex}
                              onClick={() => setEditingAdviser((current) => current === rowIndex ? "" : rowIndex)}
                            >
                              <EditIcon aria-hidden="true" />
                            </button>
                          </td>
                        )}
                      </Fragment>
                    ))}</tr>
                    {editingAdviser === rowIndex && (
                      <tr className="quilter-edit-row"><td colSpan={tableStatement.headers.length + 1}>
                        <div className="quilter-adviser-edit">
                          <span className="FP_Help_Text_Semibold">Linked adviser for {row.SourceAdviserName}</span>
                          <CustomSelect
                            options={adviserOptions}
                            value={row.LinkedAdviserEmail}
                            placeholder="Select adviser email"
                            disabled={savingAdviser}
                            onChange={(event) => changeLinkedAdviser(row, event.target.value)}
                          />
                        </div>
                      </td></tr>
                    )}
                  </Fragment>
                ))}</tbody>
                <tfoot><tr>{tableStatement.headers.map((header) => (
                  <Fragment key={header}>
                    <td className={`FP_Help_Text_Semibold ${isAmount(header) ? "text-align-right" : ""}`}>
                      {header === "Index"
                        ? "Total"
                        : isAmount(header)
                          ? formatCurrency(statementTotals[header])
                          : ""}
                    </td>
                    {header === "Index" && <td className="quilter-action-column"></td>}
                  </Fragment>
                ))}</tr></tfoot>
              </table>
            </div>
          </section>
        </>
      )}

      {isUploadOpen && (
        <div className="quilter-upload-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && closeUpload()}>
          <div className="quilter-upload-modal column gap-l" role="dialog" aria-modal="true" aria-labelledby="suspense-upload-title">
            <div className="row space-between align-center gap-m">
              <div>
                <h3 id="suspense-upload-title" className="FP_H3">{statement ? "Reupload" : "Upload"} Suspense statement</h3>
                <p className="FP_Content">This statement will be saved against {selectedMonthLabel}.</p>
              </div>
              <button type="button" className="quilter-upload-close" onClick={closeUpload} aria-label="Close upload dialog">×</button>
            </div>
            <FileUpload
              label="Statement file"
              description="Drag and drop a CSV or Excel file here or"
              browseText="Browse files"
              accept=".csv,.xlsx,.xls"
              files={selectedFile ? [selectedFile] : []}
              onFilesChange={(file) => {
                setSelectedFile(file);
                setUploadError("");
              }}
              disabled={uploading}
              status={uploadError ? "error" : "default"}
              statusMessage={uploadError}
            />
            {selectedFile && <UploadedFileList files={[selectedFile]} onRemove={() => setSelectedFile(null)} />}
            <div className="row justify-flex-end gap-m">
              <PrimaryButton
                type="button"
                label={uploading ? "IMPORTING..." : statement ? "REUPLOAD" : "UPLOAD"}
                onClick={uploadStatement}
                disabled={!selectedFile || uploading}
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default withMonthlyPaymentsAccess(SuspenseSummary);
